//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { isObjectWithProperty } from '@utils/types/typeGuards';

export function isDefined<T>(item: T | undefined): item is T {
  return item !== undefined;
}

export function isNotEmptyObject<T extends object>(item: T) {
  return Object.keys(item).length > 0;
}

export function isUndefined<T>(item: T | undefined): item is undefined {
  return item === undefined;
}

export function isNotNull<T>(item: T | null): item is T {
  return item !== null;
}

type FalsyTypes = null | undefined | '' | 0 | false;

export function isTruthy<T>(item: T): item is Exclude<T, FalsyTypes> {
  return !!item;
}

export function isNumber(item: unknown): item is number {
  return typeof item === 'number';
}

/**
 * Checks if a value is empty (undefined, null, or an empty string).
 *
 * @param value - The value to check.
 * @returns True if the value is empty, false otherwise.
 */
export const isEmptyValue = (
  value: unknown,
): value is '' | null | undefined => {
  return isUndefined(value) || value === '' || !isNotNull(value);
};

/**
 * Creates a filter predicate for the given fields.
 */
export function createHasProperty<T, P extends keyof T>(...properties: P[]) {
  return (item?: T): item is T & Required<Pick<T, P>> => {
    return properties.every((p) => isObjectWithProperty(p, item));
  };
}

/**
 * Creates an object composed of the picked `object` properties.
 */
export function pickKeys<T extends object, K extends keyof T>(
  object: T,
  keys: K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    // Check if the key exists in the object
    if (key in object) {
      result[key] = object[key];
    }
  }
  return result;
}
